import { type SearchInterface } from "./SearchInterface";

export interface SerializedSearchIndexData<TIndex extends string | number = string> {
    shortIdMap: Record<TIndex, number>;
    map: Record<string, Set<number>>;
}

export interface SerializedSearchIndex {
    version: string;
    lastUpdated: number;
    data: SerializedSearchIndexData;
}

type IndexProfile = "memory";
type DefaultTokenizer = "strict" | "forward" | "full";
type DefaultEncoder = "icase" | "simple" | "advanced" | "extra" | "balance";
type EncoderFn = (str: string) => string;

interface SearchOptions {
    id?: number;
    tokenize: DefaultTokenizer;
    split: RegExp;
    encode: DefaultEncoder | EncoderFn | false;
    cache: boolean | number;
}

export class InMemorySearch<TIndex extends string | number = string> implements SearchInterface<TIndex> {
    /**
     * The default options for the search
     * @protected
     */
    protected static defaults: SearchOptions = {
        encode  : "icase",
        tokenize: "forward",
        split   : /\W+/,
        cache   : false,
    };

    /**
     * The default options for each of the search presets
     * @protected
     */
    public static presets: Record<IndexProfile, Partial<SearchOptions>> = {
        memory: {
            encode  : "icase",
            tokenize: "strict",
        },
    };

    /**
     * Shortcuts to make it easy to reuse regex without having to redeclare it in different places
     * @private
     */
    private static regexShortcuts = {
        whitespace: /\s+/g,
        strip     : /[^a-z0-9 ]/g,
        space     : /[-/]/g,
        a         : /[àáâãäå]/g,
        e         : /[èéêë]/g,
        i         : /[ìíîï]/g,
        o         : /[òóôõöő]/g,
        u         : /[ùúûüű]/g,
        y         : /[ýŷÿ]/g,
        n         : /ñ/g,
        c         : /[çc]/g,
        s         : /ß/g,
        and       : / & /g,
        ae        : /ae/g,
        ai        : /ai/g,
        ay        : /ay/g,
        ey        : /ey/g,
        oe        : /oe/g,
        ue        : /ue/g,
        ie        : /ie/g,
        sz        : /sz/g,
        zs        : /zs/g,
        ck        : /ck/g,
        cc        : /cc/g,
        sh        : /sh/g,
        th        : /th/g,
        dt        : /dt/g,
        ph        : /ph/g,
        pf        : /pf/g,
        ou        : /ou/g,
        uo        : /uo/g,
    };

    /**
     * Regex pairs to replace for the simple replacement method
     * @private
     */
    private static simpleRegexPairs: Array<[RegExp, string]> = [
        [ this.regexShortcuts.a, "a" ],
        [ this.regexShortcuts.e, "e" ],
        [ this.regexShortcuts.i, "i" ],
        [ this.regexShortcuts.o, "o" ],
        [ this.regexShortcuts.u, "u" ],
        [ this.regexShortcuts.y, "y" ],
        [ this.regexShortcuts.n, "n" ],
        [ this.regexShortcuts.c, "k" ],
        [ this.regexShortcuts.s, "s" ],
        [ this.regexShortcuts.and, " and " ],
        [ this.regexShortcuts.space, " " ],
        [ this.regexShortcuts.strip, "" ],
        [ this.regexShortcuts.whitespace, " " ],
    ];

    /**
     * Regex pairs to replace for the advanced replacement method
     * @private
     */
    private static advancedRegexPairs: Array<[RegExp, string]> = [
        ...this.simpleRegexPairs,
        [ this.regexShortcuts.ae, "ae" ],
        [ this.regexShortcuts.ai, "ai" ],
        [ this.regexShortcuts.ay, "ay" ],
        [ this.regexShortcuts.ey, "ey" ],
        [ this.regexShortcuts.oe, "oe" ],
        [ this.regexShortcuts.ue, "ue" ],
        [ this.regexShortcuts.ie, "ie" ],
        [ this.regexShortcuts.sz, "sz" ],
        [ this.regexShortcuts.zs, "zs" ],
        [ this.regexShortcuts.ck, "ck" ],
        [ this.regexShortcuts.cc, "cc" ],
        [ this.regexShortcuts.sh, "sh" ],
        [ this.regexShortcuts.th, "th" ],
        [ this.regexShortcuts.dt, "dt" ],
        [ this.regexShortcuts.ph, "ph" ],
        [ this.regexShortcuts.pf, "pf" ],
        [ this.regexShortcuts.ou, "ou" ],
        [ this.regexShortcuts.uo, "uo" ],
    ];

    /**
     * Regex pairs to replace for the extra replacement method
     * These replace letters that sound like other letters
     * @private
     */
    private static extraRegexPairs: Array<[RegExp, string]> = [
        [ /p/g, "b" ],
        [ /z/g, "z" ],
        [ /[cgq]/g, "k" ],
        [ /n/g, "m" ],
        [ /d/g, "t" ],
        [ /[vw]/g, "f" ],
        [ /[aeiouy]/g, "" ],
    ];

    /**
     * Function which takes a list of regex and replaces it with the provided string
     * @param value
     * @param pairs
     */
    protected static replaceRegexPairs = (value: string, pairs: Array<[RegExp, string]>): string => {
        for(let i = 0, l = pairs.length; i < l; i++) {
            value = value.replace(pairs[i][0], pairs[i][1]);
        }

        return value;
    };

    /**
     * Collapse any repeating characters around the character "h"?
     * Not exactly sure what this is doing yet
     * @param value
     */
    protected static collapseRepeatingCharacters = (value: string): string => {
        let collapsedString = "";
        let charPrev = "";
        let charNext = "";

        for(let i = 0, l = value.length; i < l; i++) {
            const char = value[i];

            if(char !== charPrev) {
                if(i && char === "h") {
                    const charPrevIsVowel = (
                        charPrev === "a" ||
                        charPrev === "e" ||
                        charPrev === "i" ||
                        charPrev === "o" ||
                        charPrev === "u" ||
                        charPrev === "y"
                    );

                    const charNextIsVowel = (
                        charNext === "a" ||
                        charNext === "e" ||
                        charNext === "i" ||
                        charNext === "o" ||
                        charNext === "u" ||
                        charNext === "y"
                    );

                    if((charPrevIsVowel && charNextIsVowel) || charPrev === " ") {
                        collapsedString += char;
                    }
                } else {
                    collapsedString += char;
                }
            }

            charNext = i === l - 1 ? "" : value[i + 1];
            charPrev = char;
        }

        return collapsedString;
    };

    /**
     * The default encoding methods that are available
     * @protected
     */
    protected static encoders: Record<DefaultEncoder, EncoderFn> = {
        icase   : value => value.toLowerCase(),
        simple  : value => this.replaceRegexPairs(value.toLowerCase(), this.simpleRegexPairs).trim(),
        advanced: (value, skipPostProcessing?: boolean) => {
            value = this.replaceRegexPairs(
                value.toLowerCase(),
                this.advancedRegexPairs
            ).trim();

            if(!skipPostProcessing && value.length > 1) {
                return this.collapseRepeatingCharacters(value);
            }

            return value;
        },
        extra: value => {
            value = this.replaceRegexPairs(value.toLowerCase(), this.advancedRegexPairs).trim();

            if(value.length > 1) {
                const split = value.split(" ");

                for(let i = 0, l = split.length; i < l; i++) {
                    const current = split[i];

                    if(current.length > 1) {
                        split[i] = current[0] + this.replaceRegexPairs(current.substring(1), this.extraRegexPairs);
                    }
                }

                return this.collapseRepeatingCharacters(split.join(" "));
            }

            return value;
        },
        balance: value => this.collapseRepeatingCharacters(
            this.replaceRegexPairs(
                value.toLowerCase(),
                [
                    [ this.regexShortcuts.space, " " ],
                    [ this.regexShortcuts.strip, "" ],
                    [ this.regexShortcuts.whitespace, " " ],
                ]
            )
        ),
    };

    protected static idCounter = 0;

    protected id: number;
    protected tokenize: DefaultTokenizer;
    protected split: RegExp;
    protected encoder: EncoderFn | false;
    protected cache: SearchCache<TIndex> | undefined;
    protected map: Record<string, Set<number>> = {};
    protected cacheIsValid = false;
    protected shortIdMap: Record<TIndex, number> = {} as Record<TIndex, number>;
    protected reverseIdMap: Record<number, TIndex> = {};
    protected currentIndex = 0;
    protected idLocations: Record<number, Array<string>> = {};

    constructor(options: Partial<SearchOptions> = {}) {
        options = {
            ...InMemorySearch.defaults,
            ...options,
        };

        this.id = options?.id ?? InMemorySearch.idCounter++;

        this.tokenize = options.tokenize ?? InMemorySearch.defaults.tokenize;
        this.split = options.split ?? InMemorySearch.defaults.split;

        this.encoder = typeof options.encode === "function" ?
            options.encode :
            options.encode === false ?
                false :
                InMemorySearch.encoders[options.encode ?? InMemorySearch.defaults.encode as DefaultEncoder];

        this.cache = (options.cache === true || typeof options.cache === "number") ?
            new SearchCache(options.cache) :
            undefined;
    }

    /**
     * Add an index into the map and calculate its contextual score
     * @param duplicates
     * @param value
     * @param id
     * @param partialScore
     * @param contextScore
     * @protected
     */
    protected addIndex(
        duplicates: Record<string, number>,
        value: string,
        id: number,
        partialScore: number,
        contextScore: number
    ): number {
        if(duplicates[value]) {
            return duplicates[value];
        }

        const thresholdOrResolution = 1 / 1.5;
        const score = partialScore ?
            (1 - thresholdOrResolution) * contextScore + thresholdOrResolution * partialScore :
            contextScore;

        duplicates[value] = score;

        if(score >= 0) {
            if(typeof this.map[value] === "undefined") {
                this.map[value] = new Set();
            }

            this.map[value].add(id);
            this.idLocations[id].push(value);
        }

        return score;
    }

    /**
     * Remove an index from the provided map
     * @param id
     * @protected
     */
    protected removeIndex(id: number): void {
        if(typeof this.idLocations[id] === "undefined") {
            return;
        }

        for(let i = 0, l = this.idLocations[id].length; i < l; i++) {
            this.map[this.idLocations[id][i]].delete(id);
        }
    }

    /**
     * Remove an item from the search
     * @param id
     */
    public remove(id: TIndex): this {
        const internalId = this.shortIdMap[id];

        if(!internalId) {
            return this;
        }

        delete this.shortIdMap[id];
        delete this.reverseIdMap[internalId];
        this.removeIndex(internalId);
        delete this.idLocations[internalId];

        this.cacheIsValid = false;

        return this;
    }

    /**
     * Add an item into the search
     * @param id
     * @param content
     */
    public add(id: TIndex, content: string | undefined): this {
        if(!content?.length) {
            return this;
        }

        let internalId = this.shortIdMap[id];

        if(!internalId) {
            internalId = ++this.currentIndex;
            this.shortIdMap[id] = internalId;
            this.reverseIdMap[internalId] = id;
            this.idLocations[internalId] = [];
        }

        if(!content.length) {
            return this;
        }

        const words = content.split(this.split);

        const dupes: Record<string, number> = {};

        for(let i = 0, l = words.length; i < l; i++) {
            let value = words[i].trim();

            if(this.encoder !== false) {
                value = this.encoder(value);
            }

            if(!value) {
                continue;
            }

            const length = value.length;
            const contextScore = (l - i) / l;
            let token = "";

            switch(this.tokenize) {
                case "forward":
                    for(let a = 0; a < length; a++) {
                        token += value[a];
                        this.addIndex(
                            dupes,
                            token,
                            internalId,
                            1,
                            contextScore
                        );
                    }

                    break;
                case "full":
                    for(let x = 0; x < length; x++) {
                        const partialScore = (length - x) / length;

                        for(let y = length; y > x; y--) {
                            token = value.substring(x, y);
                            this.addIndex(
                                dupes,
                                token,
                                internalId,
                                partialScore,
                                contextScore
                            );
                        }
                    }

                    break;
                default:
                    this.addIndex(
                        dupes,
                        value,
                        internalId,
                        1,
                        contextScore
                    );
            }
        }

        this.cacheIsValid = false;

        return this;
    }

    /**
     * Sort an array by its internal lengths
     * @param a
     * @param b
     * @protected
     */
    protected sortByLengthDown(a: { length: number; }, b: { length: number; }): number {
        const diff = a.length - b.length;

        return diff < 0 ? 1 : diff ? -1 : 0;
    }

    /**
     * Intersect the arrays, in Flexsearch this is a super complex function, but it looks
     * like it doesn't get called with the complications when we use it, so I've significantly
     * simplified it lol
     * @param arrays
     * @protected
     */
    protected intersect(arrays: Array<Set<number>>): Array<number> {
        if(arrays.length === 0) {
            return [];
        } else if(arrays.length === 1) {
            return Array.from(arrays[0]);
        }

        let base = arrays[0];

        for(let i = 1, l = arrays.length; i < l; i++) {
            if(arrays[i].size === 0) {
                return [];
            }

            base = new Set(Array.from(arrays[i]).filter(x => base.has(x)));
        }

        return Array.from(base);
    }

    /**
     * Search the indexes
     * @param query
     */
    public async search(query: string): Promise<Array<TIndex>> {
        if(!query) {
            return [];
        }

        if(this.cacheIsValid && this.cache) {
            const cached = this.cache.get(query);

            if(cached) {
                return cached;
            }
        } else if(this.cache) {
            this.cache.clear();
            this.cacheIsValid = true;
        }

        if(!query.length) {
            return [];
        }

        const words = query.split(this.split);

        if(words.length > 1) {
            words.sort(this.sortByLengthDown);
        }

        let found = true;
        const checkWords: Record<string, number> = {};
        let results: Array<number> = [];
        const wordLength = words.length;
        const check: Array<Set<number>> = [];

        for(let i = 0; i < wordLength; i++) {
            let value = words[i].trim();

            if(this.encoder) {
                value = this.encoder(value);
            }

            if(!value) {
                continue;
            }

            if(!checkWords[value]) {
                const mapCheck = [];
                let mapFound = false;
                let count = 0;

                const mapValue = this.map[value];

                if(mapValue) {
                    mapCheck[count++] = mapValue;
                    mapFound = true;
                }

                if(mapFound) {
                    check.push(mapValue);
                } else {
                    found = false;
                    break;
                }

                checkWords[value] = 1;
            }
        }

        if(found) {
            results = this.intersect(check);
        }

        const mappedResults = results.map(result => this.reverseIdMap[result]);

        if(this.cache) {
            this.cache.set(query, mappedResults);
        }

        return mappedResults;
    }

    public destroy(): this {
        return this.empty();
    }

    public empty(): this {
        this.cache?.clear();
        this.cache = undefined;
        this.map = {};
        this.shortIdMap = {} as Record<TIndex, number>;
        this.reverseIdMap = {};
        this.idLocations = {};

        return this;
    }

    public import(data: SerializedSearchIndexData<TIndex>): this {
        this.cacheIsValid = false;
        this.cache?.clear();

        this.map = data.map;
        this.shortIdMap = data.shortIdMap;

        this.reverseIdMap = {};

        for(const id in this.shortIdMap) {
            if(!Object.prototype.hasOwnProperty.call(this.shortIdMap, id)) {
                continue;
            }

            this.reverseIdMap[this.shortIdMap[id]] = id;
            this.currentIndex = Math.max(this.currentIndex, this.shortIdMap[id]);
        }

        this.idLocations = {};

        for(const key in this.map) {
            if(!Object.prototype.hasOwnProperty.call(this.map, key)) {
                continue;
            }

            for(const id of this.map[key]) {
                if(typeof this.idLocations[id] === "undefined") {
                    this.idLocations[id] = [];
                }

                this.idLocations[id].push(key);
            }
        }

        return this;
    }

    public export(): SerializedSearchIndexData {
        return {
            shortIdMap: this.shortIdMap,
            map       : this.map,
        };
    }
}

class SearchCache<TIndex> {
    protected limit: number;
    protected cache = new Map<string, Array<TIndex>>();

    constructor(limit: number | true) {
        this.limit = limit === true ? Infinity : limit;
    }

    /**
     * Clear the cache
     */
    public clear(): void {
        this.cache = new Map();
    }

    /**
     * Add an item into the cache
     * @param key
     * @param value
     */
    public set(key: string, value: Array<TIndex>): void {
        if(this.cache.has(key)) {
            // Evict it in order to move the key to the end
            this.cache.delete(key);
        } else if(this.cache.size === this.limit) {
            this.cache.delete(this.cache.keys().next().value);
        }

        this.cache.set(key, value);
    }

    /**
     * Retrieve an item from the cache
     * @param key
     */
    public get(key: string): Array<TIndex> | undefined {
        const cached = this.cache.get(key);

        if(cached) {
            // Move the key to the end
            this.cache.delete(key);
            this.cache.set(key, cached);
        }

        return cached;
    }
}
